// src/components/HelpSystem.js - Updated with icons and carousel functionality
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  User,
  Link,
  Image,
  Palette,
  Layout,
  Ghost,
  Link2,
  CreditCard,
} from "lucide-react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import styles from "./HelpSystem.module.css";

// Use forwardRef to expose methods to the parent component
const HelpSystem = forwardRef(
  ({ userId, activeSection, setActiveSection }, ref) => {
    const [showHelp, setShowHelp] = useState(false);
    const [currentTip, setCurrentTip] = useState(0);
    const [hasSeenHelp, setHasSeenHelp] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const carouselRef = useRef(null);

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      openHelp: () => {
        setShowHelp(true);
        // Start with current section if available
        if (activeSection && tips[activeSection]) {
          const index = tipsArray.findIndex((tip) => tip.id === activeSection);
          if (index !== -1) {
            setCurrentTip(index);
          }
        }
      },
    }));

    // Map of icon names to Lucide icon components
    const iconComponents = {
      User: User,
      Link: Link,
      Image: Image,
      Palette: Palette,
      Layout: Layout,
      Ghost: Ghost,
      Link2: Link2,
      CreditCard: CreditCard,
    };

    // Tips database with section-specific information
    const tips = {
      details: {
        title: "Profile Details",
        description:
          "In the details tab, you can set your screen name, email and phone number (optional), bio and text colour that your profile will show. Here you also can upload a profile and banner picture.",
        icon: "User",
      },
      links: {
        title: "Manage Links",
        description:
          "Add links to websites, social media, or any URL. These appear as link buttons on your profile for visitors to click. Pro features here include link ordering, hiding, pinning and the ghost feature on link level which allows you to password protect your links for privacy or promotion. You can also add images to your links!",
        icon: "Link",
      },
      icons: {
        title: "Social Icons",
        description:
          "Add icons for your social media profiles. These appear more compact than regular links. You will also as a pro user be able to colour individual icons. Dont forget to add URLs to your links or they wont appear on your profile!",
        icon: "Image",
      },
      palette: {
        title: "Color Palette",
        description:
          "Choose colors for your profile. The palette determines the colors of backgrounds when basic themes are selected.",
        icon: "Palette",
      },
      theme: {
        title: "Profile Theme",
        description:
          "Select a visual theme for your profile. Themes affect layout, fonts, and the overall look and feel. Some themes have background images, others have not, these themes will use your chosen palatte as the color scheme.",
        icon: "Layout",
      },
      ghost: {
        title: "Ghost Mode",
        description:
          "Ghost Mode is a unique and powerful privacy feature, it allows you to password protect your whole profile offering privacy and protection for your links. This is useful for sharing content with only those that you share your password with.",
        icon: "Ghost",
      },
      shortener: {
        title: "Link Shortener",
        description:
          "Create short shareable links that redirect to longer URLs. Coupled with your link manager this feature allows you to collect analytics and also share QR codes of your links.",
        icon: "Link2",
      },
      subscription: {
        title: "Subscription",
        description:
          "Manage your subscription settings, view billing information, and check your current plan status.",
        icon: "CreditCard",
      },
    };

    // Convert tips object to array for easy navigation
    const tipsArray = Object.keys(tips).map((key) => ({
      id: key,
      ...tips[key],
    }));

    // Check if user has seen help before
    useEffect(() => {
      const checkHelpStatus = async () => {
        try {
          const userDocRef = doc(db, "users", userId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Check if the user has a preference saved
            if (userData.preferences?.hasSeenHelp !== undefined) {
              setHasSeenHelp(userData.preferences.hasSeenHelp);
              // Only show automatically to new users
              setShowHelp(
                !userData.preferences.hasSeenHelp &&
                  !userData.preferences.dontShowHelp
              );
            } else {
              // First time user, show help
              setShowHelp(true);
            }

            if (userData.preferences?.dontShowHelp) {
              setDontShowAgain(userData.preferences.dontShowHelp);
            }
          }
        } catch (error) {
          console.error("Error checking help status:", error);
        }
      };

      if (userId) {
        checkHelpStatus();
      }
    }, [userId]);

    // Save user's help preference
    const saveHelpPreference = async (seen, dontShow) => {
      try {
        const userDocRef = doc(db, "users", userId);
        await updateDoc(userDocRef, {
          "preferences.hasSeenHelp": seen,
          "preferences.dontShowHelp": dontShow,
        });
      } catch (error) {
        console.error("Error saving help preference:", error);
      }
    };

    const closeHelp = () => {
      setShowHelp(false);
      if (!hasSeenHelp) {
        setHasSeenHelp(true);
        saveHelpPreference(true, dontShowAgain);
      }
    };

    const handleDontShowAgain = (e) => {
      const checked = e.target.checked;
      setDontShowAgain(checked);
      saveHelpPreference(true, checked);
    };

    const handleNavigate = (direction) => {
      if (direction === "next") {
        setCurrentTip((prev) => (prev + 1) % tipsArray.length);
      } else {
        setCurrentTip(
          (prev) => (prev - 1 + tipsArray.length) % tipsArray.length
        );
      }
    };

    const navigateToSection = (sectionId) => {
      setActiveSection(sectionId);
      closeHelp();
    };

    // Touch event handlers for mobile swipe
    const handleTouchStart = (e) => {
      setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
      setTouchEndX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStartX || !touchEndX) return;

      const diffX = touchStartX - touchEndX;
      const threshold = 50; // minimum distance to be considered a swipe

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          // Swiped left, show next tip
          handleNavigate("next");
        } else {
          // Swiped right, show previous tip
          handleNavigate("prev");
        }
      }

      // Reset values
      setTouchStartX(null);
      setTouchEndX(null);
    };

    // Get the current tip's icon component
    const getCurrentIcon = () => {
      const iconName = tipsArray[currentTip]?.icon || "User";
      const IconComponent = iconComponents[iconName];
      return IconComponent ? <IconComponent size={28} /> : null;
    };

    return (
      <>
        {/* Help overlay */}
        {showHelp && (
          <div className={styles.helpOverlay}>
            <div
              className={styles.helpContent}
              ref={carouselRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <button className={styles.closeButton} onClick={closeHelp}>
                <X size={24} />
              </button>

              <div className={styles.iconTitle}>
                <div className={styles.iconContainer}>{getCurrentIcon()}</div>
                <h2>{tipsArray[currentTip].title}</h2>
              </div>

              <p>{tipsArray[currentTip].description}</p>

              <button
                className={styles.tryButton}
                onClick={() => navigateToSection(tipsArray[currentTip].id)}
              >
                Try it now
              </button>

              <div className={styles.navigationControls}>
                <button
                  className={styles.navButton}
                  onClick={() => handleNavigate("prev")}
                  aria-label="Previous tip"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className={styles.indicators}>
                  {tipsArray.map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.indicator} ${
                        index === currentTip ? styles.active : ""
                      }`}
                      onClick={() => setCurrentTip(index)}
                    />
                  ))}
                </div>

                <button
                  className={styles.navButton}
                  onClick={() => handleNavigate("next")}
                  aria-label="Next tip"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className={styles.swipeInstruction}>
                <span>Swipe to navigate</span>
              </div>

              <div className={styles.dontShowContainer}>
                <label>
                  <input
                    type="checkbox"
                    checked={dontShowAgain}
                    onChange={handleDontShowAgain}
                  />
                  Don't show these tips again
                </label>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default HelpSystem;
